import { Alignment, getTheme, Pivot, PivotItem, Stack } from "@fluentui/react";
import styled, { CSSProperties } from "styled-components";
import { ResponsiveGridLayout } from "@cayo/core/components/Layout/ResponsiveGridLayout";
import { IGridLayout } from "@cayo/shared/api/types/schema";
import { IRowLayout, ITabLayout } from "../../../api/schema.api";
import Grid from "../../Layout/Grid";

export interface ILayoutHierarchy {
  render: () => JSX.Element;
  name?: string;
  index: number;
}

const { semanticColors } = getTheme();

const GridRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: -15px;
  margin-right: -15px;

  &::after {
    content: "";
    display: table;
    clear: both;
  }
`;

const row = (props: IRowLayout, cells: ILayoutHierarchy[], useSizes?: boolean): JSX.Element => {
  const actualAlign = props.justifyContent as unknown as Alignment;

  const stretchStyles: CSSProperties = props.stretch
    ? { flexGrow: 1, height: "100%", maxWidth: "100%" }
    : {};

  // If sizes are provided, use Bootstrap-style grid system
  if (useSizes && props.sizes && props.sizes.length > 0) {
    const layoutKey = getLayoutKey("row", props);
    return (
      <GridRow key={layoutKey}>
        {cells.map((cell, index) => {
          const sizeClass = props.sizes?.[index] ?? "col-xs-12";
          return (
            <div key={"l" + (cell.name ?? index)} className={sizeClass}>
              {cell.render()}
            </div>
          );
        })}
      </GridRow>
    );
  }

  return (
    <Stack
      verticalAlign={props?.valign ? "center" : ((props?.verticalAlign as Alignment) ?? "start")}
      horizontal
      styles={{
        root: { display: "flex", width: "100%", ...(stretchStyles as {}) },
      }}
      horizontalAlign={actualAlign}
      tokens={{
        childrenGap: props.childrenGap,
      }}
      key={getLayoutKey("row", props)}
    >
      {cells.map((r) => {
        return r.render();
      })}
    </Stack>
  );
};

const grid = (props: IGridLayout, cells: ILayoutHierarchy[]): JSX.Element => {
  return <Grid key={getLayoutKey("grid", props)} {...props} cells={cells} />;
};

const tab = (props: ITabLayout, cells: ILayoutHierarchy[]): JSX.Element => {
  return (
    <Pivot
      key={getLayoutKey("tab", props)}
      linkFormat="tabs"
      styles={{
        itemContainer: { padding: 10, border: `1px solid ${semanticColors.disabledBorder}` },
      }}
    >
      {props.items.map((i) => (
        <PivotItem key={i.title} headerText={i.title} itemKey={i.title} alwaysRender>
          {i.items.map((itemName) => cells.find((c) => c.name === itemName)?.render())}
        </PivotItem>
      ))}
    </Pivot>
  );
};

const responsivegrid = (props: IGridLayout, cells: ILayoutHierarchy[]): JSX.Element => (
  <ResponsiveGridLayout key={getLayoutKey("responsivegrid", props)}>
    {cells.map((cell) => cell.render())}
  </ResponsiveGridLayout>
);

const getLayoutKey = (prefix: string, props: { name?: string }): string =>
  `${prefix}Layout-${props.name ?? "unnamed"}`;

export const layoutRenderers = {
  row,
  grid,
  tab,
  responsivegrid,
};

export type layoutTypes = keyof typeof layoutRenderers;
